import { Button, Checkbox, Modal, Table, Tooltip } from "antd";
import { EyeOutlined, UploadOutlined } from "@ant-design/icons";
import React, { useEffect, useRef, useState } from "react";
import {
  BillItemChecklistEntry,
  ClaimExtraDocument,
} from "./BillRequiredDocumentModal";
import { errorMessage } from "../../utils/notificationUtils";

type SupportingDocumentChecklistModalProps = {
  visible: boolean;
  documents: any[];
  claimExtraDocuments: ClaimExtraDocument[];
  portalSubmission: boolean;
  onCancel: () => void;
  onFileSelect: (
    doc: any,
    checkedItems: BillItemChecklistEntry[],
    file: File,
  ) => void;
  onView: (doc: any) => void;
};

type UploadedDocEntry = {
  documentUrl: string;
  documentName?: string;
  id?: number;
};

const SupportingDocumentChecklistModal: React.FC<
  SupportingDocumentChecklistModalProps
> = ({
  visible,
  documents,
  claimExtraDocuments,
  portalSubmission,
  onCancel,
  onFileSelect,
  onView,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingUploadRef = useRef<{
    doc: any;
    checkedItems: BillItemChecklistEntry[];
  } | null>(null);

  const [checkedState, setCheckedState] = useState<
    Record<string, BillItemChecklistEntry[]>
  >({});

  const getDocKey = (doc: any) => String(doc?.id ?? doc?.label);

  const getUploadedDocumentList = (doc: any): UploadedDocEntry[] => {
    if (doc?.documentList?.length) {
      return doc.documentList.filter((entry: any) => entry?.documentUrl);
    }

    return (
      claimExtraDocuments
        ?.filter(
          (entry) =>
            entry?.documentName?.toUpperCase() === doc?.label?.toUpperCase() ||
            entry?.documentLabelType?.toUpperCase() ===
              doc?.label?.toUpperCase(),
        )
        .map((entry) => ({
          documentUrl: entry.documentUrl,
          documentName: entry.documentName,
          id: entry.id,
        })) || []
    );
  };

  const hasUploadedDocuments = (doc: any) =>
    getUploadedDocumentList(doc).length > 0;

  const getSavedItemNamesForDoc = (
    doc: any,
  ): BillItemChecklistEntry[] | undefined => {
    const matching = claimExtraDocuments?.filter(
      (entry) =>
        entry?.documentName?.toUpperCase() === doc?.label?.toUpperCase() ||
        entry?.documentLabelType?.toUpperCase() === doc?.label?.toUpperCase(),
    );
    if (matching?.length) {
      return matching[matching.length - 1]?.itemNames;
    }
    return doc?.itemNameEntries;
  };

  const buildItemChecklistState = (
    doc: any,
    isUploaded: boolean,
  ): BillItemChecklistEntry[] => {
    const savedItemNames = getSavedItemNamesForDoc(doc);

    return (
      doc?.itemNames?.map((itemName: string) => {
        const saved = savedItemNames?.find(
          (entry) => entry.itemName === itemName,
        );

        if (isUploaded) {
          return { itemName, checked: Boolean(saved?.checked) };
        }

        return { itemName, checked: true };
      }) || []
    );
  };

  useEffect(() => {
    if (!visible) return;

    const initial: Record<string, BillItemChecklistEntry[]> = {};
    documents?.forEach((doc) => {
      const key = getDocKey(doc);
      initial[key] = buildItemChecklistState(doc, hasUploadedDocuments(doc));
    });
    setCheckedState(initial);
  }, [visible, documents, claimExtraDocuments]);

  const handleToggle = (doc: any, itemName: string, checked: boolean) => {
    if (portalSubmission || hasUploadedDocuments(doc)) return;

    const key = getDocKey(doc);
    setCheckedState((prev) => ({
      ...prev,
      [key]:
        prev[key]?.map((entry) =>
          entry.itemName === itemName ? { ...entry, checked } : entry,
        ) || [],
    }));
  };

  const isItemDisabled = (doc: any) =>
    portalSubmission || hasUploadedDocuments(doc);

  const handleUploadClick = (record: any) => {
    const checkedItems = checkedState[getDocKey(record)] || [];
    if (!checkedItems.some((entry) => entry.checked)) {
      errorMessage("Please select at least one bill item before uploading");
      return;
    }
    pendingUploadRef.current = { doc: record, checkedItems };
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file && pendingUploadRef.current) {
      onFileSelect(
        pendingUploadRef.current.doc,
        pendingUploadRef.current.checkedItems,
        file,
      );
    }
    pendingUploadRef.current = null;
    event.target.value = "";
  };

  const columns = [
    {
      title: "Document Name",
      dataIndex: "label",
      key: "label",
      width: 280,
      render: (_: string, record: any) => (
        <Tooltip title={record?.label}>
          <span>{record?.label}</span>
        </Tooltip>
      ),
    },
    {
      title: "Item Name",
      dataIndex: "itemNames",
      key: "itemNames",
      render: (_: string, record: any) => {
        const key = getDocKey(record);
        const entries = checkedState[key] || [];
        const disabled = isItemDisabled(record);
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {entries.map((entry) => (
              <Checkbox
                key={entry.itemName}
                checked={entry.checked}
                disabled={disabled}
                className={disabled ? "supporting-doc-item-locked" : undefined}
                onChange={(e) =>
                  handleToggle(record, entry.itemName, e.target.checked)
                }
              >
                <span className="supporting-doc-item-name">
                  {entry.itemName}
                </span>
              </Checkbox>
            ))}
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_: unknown, record: any) => {
        if (hasUploadedDocuments(record)) {
          return (
            <Button
              type="primary"
              icon={<EyeOutlined />}
              style={{ backgroundColor: "rgb(0, 76, 143)" }}
              disabled={portalSubmission}
              onClick={() => onView(record)}
            />
          );
        }
        return (
          <Button
            type="primary"
            icon={<UploadOutlined />}
            style={{ backgroundColor: "rgb(0, 76, 143)" }}
            disabled={portalSubmission}
            onClick={() => handleUploadClick(record)}
          >
            Upload
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <style>
        {`
          .supporting-document-checklist-modal .supporting-doc-item-name,
          .supporting-document-checklist-modal .supporting-doc-item-locked > span:last-child {
            color: rgba(0, 0, 0, 0.88) !important;
          }
          .supporting-document-checklist-modal .supporting-doc-item-locked.ant-checkbox-wrapper-disabled {
            color: rgba(0, 0, 0, 0.88) !important;
            cursor: default;
          }
        `}
      </style>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleFileInputChange}
      />
      <Modal
        title="Supporting Document Checklist"
        open={visible}
        onCancel={onCancel}
        footer={null}
        width={1100}
        destroyOnClose
        wrapClassName="supporting-document-checklist-modal"
      >
        <Table
          columns={columns}
          dataSource={documents}
          rowKey={(record) => getDocKey(record)}
          pagination={false}
          scroll={{ y: 420 }}
        />
      </Modal>
    </>
  );
};

export default SupportingDocumentChecklistModal;
